<template>
  <div class="flight-pad-vertical-path">
    <div
      v-for="(segment, segment_index) in itinerary.segments"
      :key="`${itinerary.id}:${segment.number}`"
    >
      <!-- start -->
      <div class="flight-pad-vertical-path__info">
        <div>
          <flight-pad-endpoint
            :info="segment.departure"
            alignment="end-reverse"
            :date_classes="`d-inline ${
              get_day_delta(
                segment_index > 0
                  ? itinerary.segments[segment_index - 1].arrival.at
                  : segment.departure.at,
                segment.departure.at
              ) > 0
                ? 'text-danger'
                : 'text-muted'
            }`"
            hide_location
          />
        </div>
        <div class="flight-pad-vertical-path__visual">
          <div class="flight-pad-vertical-path__visual__icon" v-html="svg_flight_start()"></div>
          <div class="flight-pad-vertical-path__visual__line"></div>
        </div>
        <div class="d-flex flex-column">
          <div style="font-size: 16px">
            ({{ segment.departure.iataCode }})
            <delta-overlay
              :text="dictionaries.locations[segment.departure.iataCode].name"
              :delta="
                get_day_delta(
                  segment_index > 0
                    ? itinerary.segments[segment_index - 1].arrival.at
                    : segment.departure.at,
                  segment.departure.at
                )
              "
              :overlay_position="{
                bottom: '7.5px',
              }"
              delta_classes="text-danger semibold"
            >
            </delta-overlay>
          </div>
          <p v-if="segment.departure.terminal" class="text-muted" style="font-size: 12px">
            terminal {{ segment.departure.terminal }}
          </p>
        </div>
      </div>
      <!-- duration connector -->
      <div class="flight-pad-vertical-path__info">
        <div style="padding: 5px 0; text-wrap: nowrap">
          <flight-pad-duration v-if="segment.duration" :duration="segment.duration" />
        </div>
        <div class="flight-pad-vertical-path__visual">
          <div
            class="flight-pad-vertical-path__visual__icon"
            v-html="svg_plane()"
            style="padding: 5px 0"
          ></div>
          <div class="flight-pad-vertical-path__visual__line"></div>
        </div>
        <div class="d-flex flex-column" style="padding: 5px 0">
          <div class="d-flex gap-5px">
            <img
              class="flight-pad-vertical-path__info__logo"
              :src="`https://static.vadobay.com/airlines-logos/200x100/${carrier_iata(
                itinerary,
                segment_index
              )}.png`"
              :alt="carrier_iata(itinerary, segment_index)"
            />
            <span class="text-muted" style="font-size: 12px; margin-top: 1px">
              volo {{ segment.number }}
            </span>
          </div>
          <p
            v-if="
              segment.operating &&
              segment.operating.carrierCode &&
              segment.operating.carrierCode !== segment.carrierCode
            "
            class="text-muted"
            style="font-size: 12px; margin: 0"
          >
            Operato da
            {{
              dictionaries.carriers[segment.operating.carrierCode] || segment.operating.carrierCode
            }}
          </p>
        </div>
      </div>
      <!-- end -->
      <div class="flight-pad-vertical-path__info">
        <div>
          <flight-pad-endpoint
            :info="segment.arrival"
            :date_classes="`d-inline ${
              get_day_delta(segment.departure.at, segment.arrival.at) > 0
                ? 'text-danger'
                : 'text-muted'
            }`"
            alignment="end-reverse"
            hide_location
          />
        </div>
        <div class="flight-pad-vertical-path__visual">
          <div class="flight-pad-vertical-path__visual__icon" v-html="svg_flight_start()"></div>
          <div
            v-if="segment_index !== itinerary.segments.length - 1"
            class="flight-pad-vertical-path__visual__line flight-pad-vertical-path__visual__dashed"
          ></div>
        </div>
        <div class="d-flex flex-column">
          <div style="font-size: 16px">
            ({{ segment.arrival.iataCode }})
            <delta-overlay
              :text="dictionaries.locations[segment.arrival.iataCode].name"
              :delta="get_day_delta(segment.departure.at, segment.arrival.at)"
              :overlay_position="{
                bottom: '7.5px',
              }"
              delta_classes="text-danger semibold"
            >
            </delta-overlay>
          </div>
          <p v-if="segment.arrival.terminal" class="text-muted" style="font-size: 12px">
            terminal {{ segment.arrival.terminal }}
          </p>
        </div>
      </div>
      <!-- stops connector -->
      <div
        v-if="segment_index !== itinerary.segments.length - 1"
        class="flight-pad-vertical-path__info"
      >
        <div>
          <!-- empty -->
        </div>

        <div class="flight-pad-vertical-path__visual">
          <div
            class="flight-pad-vertical-path__visual__line flight-pad-vertical-path__visual__dashed"
          ></div>
        </div>
        <div style="margin-top: -15px">
          <flight-pad-stops
            :itinerary="itinerary"
            :duration="get_stop_delta(segment, itinerary.segments[segment_index + 1])"
            hide_quantity
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { flight_start, plane } from '@/components/svg/flight';
import { carrier_iata, get_day_delta } from '../utilities';
import { encode_flight_offers_duration } from '@/utilities';
import FlightPadEndpoint from '@/components/blocks/flight-pad/flight-pad-endpoint.vue';
import FlightPadDuration from '@/components/blocks/flight-pad/flight-pad-duration.vue';
import FlightPadStops from '@/components/blocks/flight-pad/flight-pad-stops.vue';
import DeltaOverlay from '@/components/blocks/flight-pad/delta-overlay.vue';
import moment from 'moment';

export default {
  name: 'flight-pad-vertical-path',
  components: {
    'flight-pad-endpoint': FlightPadEndpoint,
    'flight-pad-duration': FlightPadDuration,
    'flight-pad-stops': FlightPadStops,
    'delta-overlay': DeltaOverlay,
  },
  props: {
    itinerary: Object,
  },
  computed: {
    dictionaries() {
      return this.$store.state.liveflight.results.dictionaries;
    },
  },
  methods: {
    svg_plane: plane,
    svg_flight_start: flight_start,
    carrier_iata,
    get_day_delta,
    get_stop_delta(segment, next_segment) {
      if (!segment || !next_segment) {
        return '';
      }
      try {
        const last_arrival = segment.arrival.at;
        const next_departure = next_segment.departure.at;
        return encode_flight_offers_duration({
          days: moment(next_departure).diff(last_arrival, 'days'),
          hours: moment(next_departure).diff(last_arrival, 'hours') % 24,
          minutes: moment(next_departure).diff(last_arrival, 'minutes') % 60,
        });
      } catch (e) {
        this.log('flight-pad-vertical-path.get_stop_delta', e);
        return '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.flight-pad-vertical-path {
  &__info {
    display: grid;
    grid-template-columns: 45px 40px auto;
    & > div:first-child {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
    &__logo {
      margin-top: -5px;
      max-height: 30px;
    }
  }

  &__visual {
    display: flex;
    flex-direction: column;
    align-items: center;

    &__line {
      margin-left: -1px;
      min-height: 50px;
      max-width: 1px;
      border: 1px solid $primary;
      border-radius: 50px;
    }
    &__dashed {
      margin-top: 1.5px;
      border: 1px dashed $primary;
    }
    &__icon {
      transform: rotate(90deg);
    }
  }
}
</style>
